// Copyright (c) 2026 PGNS LLC

import type { Environment } from '@pgns/gen/config/urls';
import { getUrls, SOCIALS } from '@pgns/gen/config/urls';

const env = (import.meta.env.VITE_PGNS_ENV || 'local') as Environment;
// Worktree port isolation: `pgns-wt new` assigns each worktree a port offset so
// several trees can run their dev servers at once. Zero in the main checkout.
const PORT_OFFSET = Number(import.meta.env.VITE_WT_PORT_OFFSET ?? 0) || 0;
const urls = getUrls(env, PORT_OFFSET);

export const HOME_URL = urls.web;
export const DOCS_URL = urls.docs;
export const DASHBOARD_URL = urls.dashboard;
export const STATUS_URL = urls.status;
export const GITHUB_URL = SOCIALS.github;

/**
 * Base URL for the status Worker API.
 *
 * Empty by default, meaning same-origin relative requests: the Worker answers
 * on the status page's own origin in production, and the Vite dev server
 * proxies `/v1` and `/feed.xml` to a local `wrangler dev` in development.
 * `VITE_STATUS_API_URL` overrides it for a preview deploy where the page and
 * the Worker sit on different hostnames (the Worker sends `access-control-
 * allow-origin: *`, so cross-origin reads work).
 */
export const STATUS_API_URL = (import.meta.env.VITE_STATUS_API_URL ?? '').replace(/\/$/, '');

/**
 * True in the Cloudflare-outage fallback bundle (PGNS-1310).
 *
 * That build is served from S3 + CloudFront with a pre-baked snapshot of the
 * status API sitting next to it as static files, so every fetch succeeds and
 * the page looks live. It is not: the probes run in the Worker this build
 * exists precisely because nobody can reach. The flag exists so the page can
 * say so on its face, which is the whole difference between a degraded status
 * page and a lying one.
 */
export const IS_FALLBACK = import.meta.env.VITE_STATUS_FALLBACK === '1';
